import { useEffect, useState, type RefObject } from 'react';
import { applyFocusHighlight, clearFocusHighlight } from '../highlightAnchors';

type FlashTarget = { text: string; occurrence: number };

/**
 * Owns the one-shot "you just landed here" flash highlight's full lifecycle —
 * e.g. after scrolling to a line from a chat anchor click or an AI row's
 * Comment click. Call the returned `flash` function with a quote to trigger;
 * a fresh object literal each call (not a memoized/interned one) is what
 * makes clicking the SAME quote twice in a row still retrigger, since a
 * bare-value update to identical `{ text, occurrence }` content would
 * otherwise let React bail on the state update.
 *
 * WhatsApp/Docs-style fade-in, hold, fade-out — not an abrupt on/off: the CSS
 * Custom Highlight API doesn't support `transition` on ::highlight() (there's
 * no persistent styled box for the browser to interpolate — a Highlight is
 * closer to a per-paint instruction), and its style whitelist excludes
 * `transform` too (no scale/pulse), so the whole animation is driven by hand
 * as a single rAF loop stepping a CSS custom property the ::highlight() rule
 * reads its alpha from every frame.
 *
 * The rAF loop is cosmetic only — the actual clear is a setTimeout for the
 * full TOTAL_MS, not "whenever the rAF loop finishes". Browsers throttle
 * (often to a full stop) requestAnimationFrame in a backgrounded/unfocused
 * tab; gating the real cleanup on it left the highlight stuck at full opacity
 * indefinitely the moment that happened (confirmed live: alpha still at 0.45
 * seconds after the flash should have cleared). setTimeout still fires
 * (coarsened, but not paused), so the highlight is always gone within the
 * stated duration regardless of tab focus; rAF just makes the visible case a
 * smooth animation instead of a snap-on/snap-off.
 */
export function useFlashHighlight(
  containerRef: RefObject<HTMLElement | null>,
  /** Extra dependency that should retrigger the effect alongside `target` —
   *  e.g. the underlying script text (translation toggle) changing under the
   *  same target. */
  extraDep?: unknown,
): (target: FlashTarget) => void {
  const [target, setTarget] = useState<FlashTarget | null>(null);

  useEffect(() => {
    if (!target) return;
    const container = containerRef.current;
    if (!container) return;
    if (!applyFocusHighlight(container, target.text, target.occurrence)) return;
    const FADE_IN_MS = 150;
    const HOLD_MS = 300;
    const FADE_OUT_MS = 1100;
    const TOTAL_MS = FADE_IN_MS + HOLD_MS + FADE_OUT_MS;
    const PEAK_ALPHA = 0.45;
    container.style.setProperty('--script-jump-flash-alpha', '0');
    const start = performance.now();
    let rafId: number | null = null;
    const step = (now: number) => {
      const elapsed = now - start;
      const alpha =
        elapsed < FADE_IN_MS
          ? PEAK_ALPHA * (elapsed / FADE_IN_MS)
          : elapsed < FADE_IN_MS + HOLD_MS
            ? PEAK_ALPHA
            : PEAK_ALPHA * Math.max(0, 1 - (elapsed - FADE_IN_MS - HOLD_MS) / FADE_OUT_MS);
      container.style.setProperty('--script-jump-flash-alpha', String(alpha));
      if (elapsed < TOTAL_MS) rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    const clearTimeoutId = setTimeout(() => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      clearFocusHighlight();
      setTarget(null);
    }, TOTAL_MS);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      clearTimeout(clearTimeoutId);
      clearFocusHighlight();
    };
    // containerRef is a ref — deliberately excluded from deps (stable by contract).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, extraDep]);

  return setTarget;
}
